import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Banknote, Package, ShoppingCart, Star, TrendingUp } from 'lucide-react';
import { Card } from '@atoms/Card';
import { Badge } from '@atoms/Badge';
import { Spinner } from '@atoms/Spinner';
import { Button } from '@atoms/Button';
import { SalesChart } from '@molecules/SalesChart';
import { wholesalerService } from '@services/wholesalerService';
import { formatCurrency, formatShortDate, ORDER_STATUS_LABELS } from '@utils/formatters';

function KpiCard({ label, value, icon: Icon, hint }: { label: string; value: string | number; icon: typeof Package; hint?: string }) {
  return (
    <Card className="card-elevated flex items-start gap-4">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-700">
        <Icon className="h-5 w-5" />
      </div>
      <div>
        <p className="text-sm text-slate-500">{label}</p>
        <p className="text-2xl font-bold text-slate-900">{value}</p>
        {hint && <p className="mt-0.5 text-xs text-slate-400">{hint}</p>}
      </div>
    </Card>
  );
}

export function OverviewSection() {
  const { data, isLoading } = useQuery({
    queryKey: ['wholesaler', 'overview'],
    queryFn: () => wholesalerService.getOverview(),
    refetchInterval: 60000,
  });

  if (isLoading) return <Spinner />;

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Genel Bakış</h1>
        <p className="mt-1 text-sm text-slate-500">Operasyon özeti ve hızlı aksiyonlar</p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
        <KpiCard
          label="Toplam Satış"
          value={formatCurrency(data?.totalRevenue ?? 0)}
          icon={Banknote}
          hint="Tamamlanan siparişler"
        />
        <KpiCard label="Bugünkü Sipariş" value={data?.ordersToday ?? 0} icon={ShoppingCart} />
        <KpiCard label="Bugünkü Gelir" value={formatCurrency(data?.revenueToday ?? 0)} icon={TrendingUp} hint="Onaylı ve tamamlanan siparişler" />
        <KpiCard label="Bekleyen Sipariş" value={data?.pendingOrders ?? 0} icon={Package} hint={`${data?.totalOrders ?? 0} toplam sipariş`} />
        <KpiCard label="Ortalama Puan" value={(data?.avgRating ?? 0).toFixed(1)} icon={Star} hint={`${data?.unreadMessages ?? 0} okunmamış mesaj`} />
      </div>

      <div className="flex flex-wrap gap-3">
        <Link to="/wholesaler/orders"><Button variant="outline">Bekleyen Siparişler</Button></Link>
        <Link to="/wholesaler/products"><Button variant="outline">Ürün Ekle</Button></Link>
        <Link to="/wholesaler/campaigns"><Button variant="outline">Kampanya Oluştur</Button></Link>
        <Link to="/wholesaler/reviews"><Button variant="outline">Değerlendirmeler</Button></Link>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card className="card-elevated">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="font-semibold text-slate-900">Son Siparişler</h2>
            <Link to="/wholesaler/orders" className="text-sm text-brand-600 hover:underline">Tümü</Link>
          </div>
          {!data?.recentOrders.length ? (
            <p className="text-sm text-slate-500">Henüz sipariş yok</p>
          ) : (
            <ul className="divide-y divide-slate-100">
              {data.recentOrders.map((order) => (
                <li key={order.id} className="flex items-center justify-between gap-3 py-3">
                  <div>
                    <p className="text-sm font-medium">#{order.id.slice(0, 8)} · {order.restaurantName ?? 'Restoran'}</p>
                    <p className="text-xs text-slate-500">{formatShortDate(order.createdAt)}</p>
                  </div>
                  <div className="text-right">
                    <Badge>{ORDER_STATUS_LABELS[order.status]}</Badge>
                    <p className="mt-1 text-sm font-medium">{formatCurrency(order.totalPrice)}</p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card className="card-elevated">
          <h2 className="mb-4 font-semibold text-slate-900">En Çok Satan Ürünler</h2>
          {!data?.topProducts.length ? (
            <p className="text-sm text-slate-500">Veri yok</p>
          ) : (
            <ul className="divide-y divide-slate-100">
              {data.topProducts.map((p, i) => (
                <li key={p.productName} className="flex items-center justify-between py-3">
                  <span className="text-sm font-medium text-slate-900">{i + 1}. {p.productName}</span>
                  <span className="text-sm text-slate-500">{p.quantity} adet</span>
                </li>
              ))}
            </ul>
          )}
          <p className="mt-4 text-xs text-slate-500">
            {data?.activeCampaigns ?? 0} aktif kampanya · {data?.inactiveProducts ?? 0} pasif ürün
          </p>
        </Card>
      </div>

      <SalesChart
        data={data?.salesChart ?? []}
        title="Satış Grafiği (7 Gün)"
        subtitle="İptal edilmeyen sipariş tutarları (bekleyen + onaylı + tamamlanan)"
      />
    </div>
  );
}
